import useFormatting from "../../../hooks/useFormatting"
import styled from "styled-components"
import { inheritData } from "../../../utils/objects/inherit"
import useSelectionState from "../../../hooks/selection/useSelectionState"
import { useEffect, useRef } from "react"
import BaseList from "./lists/BaseList"
import SelectList from "./lists/SelectList"

/**
 * A list of rows inside a stateForm, one of which is picked.
 *
 * It was `pluriList`, which said nothing. `cardList` would have been wrong —
 * only one of its two configs is cards; the other renders `BaseList`, the same
 * leaf `multiSelect` uses. And `selectList` is already taken, by the leaf this
 * renders and by the `listType` value that selects it, so the widget and its own
 * leaf would have shared a name one nesting level apart.
 *
 * What it does in both modes is let you pick a row and tell its host which one.
 * Task 34.2 measures what it would take to fold it into `multiSelect`, and
 * recommends waiting: it has one caller.
 *
 * Where the rows come from used to be three branches — `endpoint`, `array` and
 * `parent` — and two of them were unreachable. Both live configs declare
 * `parent`, and the `useQuery` was `enabled` only for `endpoint`, so the query,
 * its `getData`, and a 60-line `generateUrl` that turned `sourceFilter` into a
 * `?filter=k:v;k:v` string were all dead — along with `sourceFilter` itself in
 * the config. That is 33.2 §1.9 in a second widget: declared, and read by
 * nothing.
 *
 * Deleted rather than migrated. When this folds into `multiSelect` it gets
 * `source` like everything else (33.2 step 10); until then, one live source
 * stated plainly beats three branches where two render nothing.
 */

const Wrapper = styled.div`
    margin: ${props => props.$margin ? props.$margin : '0'};
`

const PickList = ({
    entity,
    validated,
    useStore,
    section,
    lineId,
    path,
    setSelected,
    data,
    onAction,
}) => {

    // The list's rows are named by the element that declares it, like every
    // other widget. This used to be the literal 'modalForm' — a component name
    // hard-coded as a test id, which is the defect task 33.1 set out to remove
    // and the one place it survived, because it is a widget's id rather than a
    // modal's.
    const testId = section?.testId ?? 'modalForm'

    // Early return logic goes here, after all hooks are called.
    const [state] = useStore((s) => s)
    const { formatData } = useFormatting()

    // `source` is still declared, and still read — by `StateFormView`, which uses
    // it to pick which loaded array to hand down as `data`. It is not read here.
    const { dataSource, listType, dataFormat, dataInherit, listId, preselectFirst } = section || {}

    let listToDiplay = listType || 'baseList'


    // Selection management using useSelectionState
    const getVisibleIds = (data2use) => (Array.isArray(data2use) ? data2use.map(i => i?.id) : [])

    const initialSelectedIds = []

    const selection = useSelectionState({
        singleSelect: section?.singleSelect ?? true,
        maxSelection: section?.maxSelection ?? 100,
        getVisibleIds,
        initialSelectedIds
    })


    if (section && dataSource !== 'parent') {
        console.warn(`[PickList] dataSource '${dataSource}' is not supported — rows come from the parent`)
    }

    let fData = data || []

    if (dataFormat && fData.length > 0) fData = formatData(fData, dataFormat)
    if (dataInherit && fData.length > 0) fData = inheritData(fData, dataInherit, state)

    // Normalize items to ensure there is a stable id for selection/rendering
    const idKey = listId || 'id'
    const dataForList = Array.isArray(fData)
        ? fData.map(item => {
            if (!item || typeof item !== 'object') return { id: null };
            const resolvedId = item[idKey] ?? item.id
            if (resolvedId === undefined) {
                // eslint-disable-next-line no-console
                console.warn('[PickList] Item missing identifier. Provide section.idField/listId or ensure items contain an id.', item)
            }
            return { ...item, id: resolvedId }
        })
        : []



    // Open with the first row chosen, when the config asks for it.
    //
    // This existed before as an effect guarded by `result.isSuccess` and by
    // `dataSource === 'array'` — two conditions no live config could satisfy —
    // so it had never run once. It came out at 33.2 step 4b with the dead fetch
    // path, deliberately rather than rewritten, because whether a list opens
    // with a row selected is a decision and not a detail. It is back as that
    // decision, declared.
    //
    // `selectList` is excluded: it preselects internally, and by a different
    // rule — the best suggestion rather than the first row — so honouring the
    // flag there would mean two components racing for the same selection.
    //
    // Keyed on the first row's id rather than on mount, so it fires when the
    // rows arrive and again if they are replaced, but never fights a choice the
    // user has already made.
    const firstId = dataForList[0]?.id ?? null
    const preselectedFor = useRef(null)
    const wantsPreselect = preselectFirst === true && listToDiplay !== 'selectList'

    useEffect(() => {
        if (!wantsPreselect || firstId === null || firstId === undefined) return
        if (preselectedFor.current === firstId) return
        if (selection.selectedItems.size > 0) return

        preselectedFor.current = firstId
        selection.toggleItem(firstId)
    }, [wantsPreselect, firstId])

    // The host gets every selected id, as a list — one with `singleSelect`
    // (the default), several without. Synced from the selection itself rather
    // than from the click, so unticking the last row tells the host too.
    // `selectList` (suggestions) keeps its selection to itself, as before.
    const reportsSelection = listToDiplay !== 'selectList' && typeof setSelected === 'function'
    useEffect(() => {
        if (reportsSelection) setSelected([...selection.selectedItems])
    }, [selection.selectedItems])

    const selectedItemObjects = Array.isArray(dataForList)
        ? dataForList.filter(item => item?.id !== null && selection.selectedItems.has(item.id))
        : []

    const handleToggleSelection = (item) => {
        if (!item?.id) return;
        selection.toggleItem(item.id)
    };

    // Guard against missing section
    if (!section) {
        console.warn('[PickList] Missing section configuration');
        return null;
    }

    return (
        <Wrapper $margin={section?.margins}>
            {(listToDiplay === 'baseList') && <BaseList
                columns={section?.columns}
                emptyMessage={section?.emptyMessage}
                data={dataForList}
                biData={[]}
                selectedItems={selectedItemObjects}
                isItemSelectable={() => true}
                handleSelection={handleToggleSelection}
                getTagValue={() => { }}
                testId={testId}
            />}
            {(listToDiplay === 'selectList') && <SelectList
                columns={section?.columns}
                emptyMessage={section?.emptyMessage}
                data={dataForList}
                biData={[]}
                selectedItems={selectedItemObjects}
                isItemSelectable={() => true}
                handleSelection={handleToggleSelection}
                getTagValue={() => { }}
                testId={testId}
                layout={section?.layout}
                action={section?.action}
                onAction={onAction}
            />}
 
        </Wrapper>
    )


}

export default PickList